import { useCallback } from 'react';
import type { KeyboardEvent, RefObject } from 'react';
import { ARROW_DOWN_KEY, ARROW_UP_KEY, ESCAPE_KEY } from 'common/constants/general';
import {
  getKeyboardNavigationIndex,
  KEYBOARD_END_KEY,
  KEYBOARD_HOME_KEY,
} from 'components/ui/utilities/keyboardNavigation';

interface UseSearchableListKeyboardNavigationOptions {
  inputRef?: RefObject<HTMLInputElement | null>;
  itemSelector: string;
  listRef: RefObject<HTMLElement | null>;
}

const isFocusableListItem = (element: HTMLElement) => {
  const isDisabledButton = element instanceof HTMLButtonElement && element.disabled;

  return element.getClientRects().length > 0
    && !isDisabledButton
    && !element.hasAttribute('aria-disabled');
};

export const useSearchableListKeyboardNavigation = <TElement extends HTMLElement = HTMLElement>({
  inputRef,
  itemSelector,
  listRef,
}: UseSearchableListKeyboardNavigationOptions) => {
  // Composite widgets keep Tab for entering/leaving the region; arrow keys move within visible items.
  const getFocusableItems = useCallback(() => {
    const listElement = listRef.current;

    if (!listElement) {
      return [];
    }

    return Array.from(listElement.querySelectorAll<TElement>(itemSelector)).filter(isFocusableListItem);
  }, [itemSelector, listRef]);

  const focusItem = useCallback((item: TElement | undefined) => {
    if (!item) {
      return false;
    }

    item.focus();
    item.scrollIntoView({ block: 'nearest' });
    return true;
  }, []);

  const focusFirstItem = useCallback(() => {
    const items = getFocusableItems();
    return focusItem(items[0]);
  }, [focusItem, getFocusableItems]);

  const focusLastItem = useCallback(() => {
    const items = getFocusableItems();
    return focusItem(items.at(-1));
  }, [focusItem, getFocusableItems]);

  const focusNavigationItem = useCallback((currentItem: TElement, key: string) => {
    const items = getFocusableItems();
    const currentIndex = items.indexOf(currentItem);
    const nextIndex = getKeyboardNavigationIndex({
      currentIndex,
      itemCount: items.length,
      key,
      loop: false,
      orientation: 'vertical',
    });

    if (nextIndex === -1) {
      return false;
    }

    return focusItem(items[nextIndex]);
  }, [focusItem, getFocusableItems]);

  const handleSearchKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ARROW_DOWN_KEY) {
      if (focusFirstItem()) {
        event.preventDefault();
      }
      return;
    }

    if (event.key === ARROW_UP_KEY && focusLastItem()) {
      event.preventDefault();
    }
  }, [focusFirstItem, focusLastItem]);

  const handleItemsKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null;
    const currentItem = target?.closest<TElement>(itemSelector);

    if (!currentItem) {
      return;
    }

    if ([ARROW_DOWN_KEY, ARROW_UP_KEY, KEYBOARD_HOME_KEY, KEYBOARD_END_KEY].includes(event.key)) {
      if (focusNavigationItem(currentItem, event.key)) {
        event.preventDefault();
      }
      return;
    }

    if (event.key === ESCAPE_KEY && inputRef?.current) {
      event.preventDefault();
      inputRef.current.focus();
    }
  }, [focusNavigationItem, inputRef, itemSelector]);

  return {
    focusFirstItem,
    handleItemsKeyDown,
    handleSearchKeyDown,
  };
};
